// frontend/src/pages/Gallery.jsx
// ✅ UPDATED: Pi SDK native download via openUrlInSystemBrowser
// ✅ NEW: Video badge on cards + video playback + download in lightbox
//         (Videos use signed URLs from /api/generations so no extra round-trip on gallery load)

import React from "react";
import { Link } from "react-router-dom";
import Screen from "../uizard/ui/Screen";
import { getIdentityHeaders, waitForIdentity } from "../lib/identity";
import { trackStyleView } from "../utils/trackStyleView.js";
import {
  API_BASE,
  normalizeListResponse,
  getImageUrl,
  toast,
} from "../lib/api";

const fmt = (d) =>
  new Date(d).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const brand = {
  purple: "#6D5EF9",
  purpleDark: "#5B4EE8",
  purpleTint: "#F3F0FF",
  purpleBorder: "#E7E2FF",
  neutralBorder: "#E5E7EB",
  shadow: "0 8px 24px rgba(17, 24, 39, 0.08)",
};

const api = (path) => (API_BASE ? `${API_BASE}${path}` : path);

// Animation gate — flip to `true` on the testnet build to disable animations
// (mirrors the Create page TESTNET_PAUSED pattern). Keep `false` on mainnet.
const ANIMATION_PAUSED = true;

export default function Gallery() {
  const [items, setItems] = React.useState([]);
  const [cursor, setCursor] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [err, setErr] = React.useState("");

  const [query, setQuery] = React.useState("");
  const [sortBy, setSortBy] = React.useState("newest");
  const [viewer, setViewer] = React.useState(null);
  const [busyId, setBusyId] = React.useState("");
  const [deletingId, setDeletingId] = React.useState("");

  const [showInfo, setShowInfo] = React.useState(() => {
    try {
      return localStorage.getItem("gallery_info_dismissed") !== "1";
    } catch {
      return true;
    }
  });

  function dismissInfo() {
    setShowInfo(false);
    try {
      localStorage.setItem("gallery_info_dismissed", "1");
    } catch {}
  }

  async function fetchPage(nextCursor) {
    const qs = new URLSearchParams();
    qs.set("limit", "12");
    if (nextCursor) qs.set("cursor", nextCursor);

    const res = await fetch(api(`/api/generations?${qs.toString()}`), {
      credentials: "include",
      headers: { ...getIdentityHeaders() },
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json?.error || res.statusText);

    return normalizeListResponse(json);
  }

  React.useEffect(() => {
    if (items && items.length > 0) {
      const uniqueStyles = new Set();
      items.forEach(item => {
        const styleSlug = String(item.style || "").toLowerCase().trim();
        if (styleSlug && styleSlug !== "default") {
          uniqueStyles.add(styleSlug);
        }
      });
      uniqueStyles.forEach(styleId => {
        trackStyleView(styleId, "gallery");
      });
    }
  }, [items]);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setErr("");
        await waitForIdentity();

        const { items: first, nextCursor } = await fetchPage(null);
        if (!alive) return;

        setItems(first);
        setCursor(nextCursor);
      } catch (e) {
        if (alive) {
          setErr(e.message || "Failed to load");
          setItems([]);
          setCursor(null);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  async function loadMore() {
    if (!cursor || loadingMore) return;
    try {
      setLoadingMore(true);
      const { items: more, nextCursor } = await fetchPage(cursor);
      setItems((prev) => [...prev, ...more]);
      setCursor(nextCursor);
    } catch (e) {
      setErr(e.message || "Failed to load more");
    } finally {
      setLoadingMore(false);
    }
  }

  const customStyles = React.useMemo(() => {
    const styles = new Set();
    items.forEach((item) => {
      const s = String(item.style || "default").toLowerCase().trim();
      if (s !== "default" && s !== "") styles.add(s);
    });
    return Array.from(styles);
  }, [items]);

  const styleCategories = React.useMemo(() => {
    const styleCounts = {};
    items.forEach((item) => {
      const s = String(item.style || "default").toLowerCase().trim();
      styleCounts[s] = (styleCounts[s] || 0) + 1;
    });

    const mostUsed = Object.entries(styleCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([style]) => style);

    const recentItems = items.slice(0, 10);
    const recentStyles = [
      ...new Set(recentItems.map((i) => String(i.style || "default").toLowerCase().trim())),
    ];

    return { mostUsed, recentStyles };
  }, [items]);

  const filtered = React.useMemo(() => {
    let list = items.slice();

    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(
        (i) =>
          (i.prompt || "").toLowerCase().includes(q) ||
          (i.style || "").toLowerCase().includes(q)
      );
    }

    switch (sortBy) {
      case "newest":
        list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case "oldest":
        list.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      case "default-only":
        list = list.filter((i) => {
          const s = String(i.style || "default").toLowerCase().trim();
          return s === "default" || s === "";
        });
        list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case "most-used":
        list = list.filter((i) =>
          styleCategories.mostUsed.includes(String(i.style || "default").toLowerCase().trim())
        );
        list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case "recent-styles":
        list = list.filter((i) =>
          styleCategories.recentStyles.includes(String(i.style || "default").toLowerCase().trim())
        );
        list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      // ✅ NEW: video-only filter
      case "with-video":
        list = list.filter((i) => i.hasVideo || i.videoUrl);
        list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      default:
        list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    return list;
  }, [items, query, sortBy, styleCategories]);

  // ✅ NEW: count animated items for the filter option enable/disable
  const videoCount = React.useMemo(
    () => items.filter((i) => i.hasVideo || i.videoUrl).length,
    [items]
  );

  async function toggleAllowOnHome(item) {
    const next = !item.allowOnHome;
    try {
      setBusyId(item.id);
      const res = await fetch(api(`/api/generations/${item.id}/allow-home`), {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getIdentityHeaders() },
        credentials: "include",
        body: JSON.stringify({ allow: next }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || res.statusText);

      setItems((prev) =>
        prev.map((it) =>
          it.id === item.id ? { ...it, allowOnHome: j.allowOnHome ?? next } : it
        )
      );
      setViewer((v) => (v && v.id === item.id ? { ...v, allowOnHome: j.allowOnHome ?? next } : v));
    } catch (e) {
      toast(e.message || "Failed to update visibility");
    } finally {
      setBusyId("");
    }
  }

  async function deleteItem(id) {
    if (!id) return;
    const yes = window.confirm("Delete this image permanently? This cannot be undone.");
    if (!yes) return;

    try {
      setDeletingId(id);
      const res = await fetch(api(`/api/generations/${id}`), {
        method: "DELETE",
        credentials: "include",
        headers: { ...getIdentityHeaders() },
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.error || res.statusText);

      setItems((prev) => prev.filter((x) => x.id !== id));
      setViewer((v) => (v && v.id === id ? null : v));
      toast("Image deleted");
    } catch (e) {
      toast(e.message || "Failed to delete image");
    } finally {
      setDeletingId("");
    }
  }

  return (
    <Screen>
      <StyleTag />

      <div className="gal-header">
        <div>
          <h1
            className="h1"
            style={{
              background:
                "var(--ab-hero-grad, linear-gradient(135deg, #1E3A8A 0%, #6D28D9 100%))",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            Your Gallery
          </h1>

          <p className="lead">
            All images <strong>you created</strong> — search, filter, or delete.
            <br />
            <span style={{ opacity: 0.8 }}>
              To view how others are using your styles, click the{" "}
              <strong>style tag</strong> (↗).
            </span>
          </p>

          {err ? <div className="err-note">⚠ {err}</div> : null}
        </div>

        <div className="gal-controls">
          <div className="gal-search">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by prompt or style…"
            />
          </div>

          <div className="gal-sort">
            <label>
              Sort & Filter
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="default-only">Default style only</option>
                <option value="most-used" disabled={styleCategories.mostUsed.length === 0}>
                  Most used styles
                </option>
                <option value="recent-styles" disabled={styleCategories.recentStyles.length === 0}>
                  Recently used styles
                </option>
                {/* ✅ NEW: video-only filter */}
                <option value="with-video" disabled={videoCount === 0}>
                  🎬 Animated only{videoCount > 0 ? ` (${videoCount})` : ""}
                </option>
              </select>
            </label>
          </div>
        </div>
      </div>

      {showInfo && customStyles.length > 0 && (
        <div className="info-banner">
          <div className="info-banner-icon">ℹ️</div>
          <div className="info-banner-content">
            <h3>Gallery vs. Generations by Style</h3>
            <p>
              <strong>This Gallery</strong> shows all images <strong>you created</strong> (with any style).
            </p>
            <p>
              <strong>Generations by Style</strong> shows images{" "}
              <strong>others created using your custom styles</strong> — useful for tracking style usage and royalties.
            </p>
            <div className="info-banner-actions">
              {customStyles.map((styleId) => (
                <Link
                  key={styleId}
                  to={`/creator/styles/${encodeURIComponent(styleId)}`}
                  className="btn btn-sm"
                >
                  View "{styleId}" usage →
                </Link>
              ))}
            </div>
          </div>
          <button className="info-banner-close" onClick={dismissInfo} title="Dismiss">
            ×
          </button>
        </div>
      )}

      {loading ? (
        <SkeletonGrid />
      ) : filtered.length === 0 ? (
        <EmptyState
          onClear={() => {
            setQuery("");
            setSortBy("newest");
          }}
          hasFilters={query.trim() !== "" || sortBy !== "newest"}
        />
      ) : (
        <>
          {(sortBy !== "newest" || query.trim()) && (
            <div className="filter-info">
              Showing {filtered.length} of {items.length} images
              {sortBy !== "newest" && (
                <span className="filter-badge">
                  {sortBy === "oldest" && "Oldest first"}
                  {sortBy === "default-only" && "Default style only"}
                  {sortBy === "most-used" && "Most used styles"}
                  {sortBy === "recent-styles" && "Recently used styles"}
                  {sortBy === "with-video" && "🎬 Animated only"}
                </span>
              )}
              {query.trim() && (
                <span className="filter-badge">Search: "{query}"</span>
              )}
              <button
                className="clear-filters"
                onClick={() => {
                  setQuery("");
                  setSortBy("newest");
                }}
              >
                Clear all
              </button>
            </div>
          )}

          <div className="masonry">
            {filtered.map((it) => (
              <MasonryCard
                key={it.id}
                item={it}
                busy={busyId === it.id}
                deleting={deletingId === it.id}
                onOpen={() => setViewer(it)}
                onToggle={() => toggleAllowOnHome(it)}
                onDelete={() => deleteItem(it.id)}
              />
            ))}
          </div>

          {cursor && (
            <div className="load-more">
              <button
                className="btn"
                onClick={loadMore}
                disabled={loadingMore}
                title="Load more images"
              >
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </>
      )}

      {viewer && (
        <Lightbox
          item={viewer}
          busy={busyId === viewer.id}
          deleting={deletingId === viewer.id}
          onClose={() => setViewer(null)}
          onToggle={() => toggleAllowOnHome(viewer)}
          onDelete={() => deleteItem(viewer.id)}
        />
      )}
    </Screen>
  );
}

function MasonryCard({ item, onOpen, onToggle, onDelete, busy, deleting }) {
  const [loaded, setLoaded] = React.useState(false);
  const url = getImageUrl(item);
  const hasVideo = !!(item.videoUrl || item.hasVideo);

  const styleId = React.useMemo(() => {
    const s = String(item.style || "").toLowerCase().trim();
    if (!s || s === "default") return "";
    return s;
  }, [item.style]);

  const onDownload = async (e) => {
    e.stopPropagation();
    
    const downloadUrl = api(`/api/generations/${item.id}/download`);
    
    if (window.Pi && typeof window.Pi.openUrlInSystemBrowser === 'function') {
      try {
        await window.Pi.openUrlInSystemBrowser(downloadUrl);
        toast("Opening in system browser for download");
      } catch (err) {
        console.error("Pi SDK download error:", err);
        if (err.message === "No minimal requirements") {
          toast("Please update Pi Browser to download images");
        } else if (err.message === "Failed to open URL") {
          toast("Invalid image URL");
        } else {
          fallbackDownload();
        }
      }
    } else {
      fallbackDownload();
    }
    
    async function fallbackDownload() {
      try {
        const res = await fetch(downloadUrl, {
          method: "GET",
          headers: { ...getIdentityHeaders() },
          credentials: "include",
        });
        if (!res.ok) throw new Error(`Download failed (${res.status})`);
        const blob = await res.blob();
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        a.download = `artibytes-${item.id}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(u);
      } catch (err) {
        toast(err.message || "Download failed");
      }
    }
  };

  return (
    <article className="masonry-card" onClick={onOpen}>
      <div className="img-wrap">
        <img
          src={url}
          alt={item.prompt || "generation"}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          fetchpriority="low"
          className={loaded ? "ready" : ""}
          onLoad={() => setLoaded(true)}
        />

        {styleId && (
          <Link
            to={`/creator/styles/${encodeURIComponent(styleId)}`}
            className="style-chip style-link"
            title={`View generations using "${styleId}"`}
            onClick={(e) => e.stopPropagation()}
          >
            <span className="style-text">{styleId}</span>
            <span className="style-ext" aria-hidden="true">↗</span>
          </Link>
        )}

        {item.allowOnHome && (
          <div className="home-chip" title="Allowed on Home">
            HOME
          </div>
        )}

        {/* ✅ NEW: Video badge — bottom-left corner, hides on hover so it doesn't fight the action bar */}
        {hasVideo && (
          <div className="video-chip" title="Has animated video — click to view">
            <span aria-hidden="true">▶</span>
            <span>Video</span>
          </div>
        )}

        <div className="hover-bar">
          <button className="action" onClick={(e) => { e.stopPropagation(); onOpen(); }}>
            View
          </button>

          <button className="action icon" onClick={onDownload} aria-label="Download" title="Download image">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M12 3v10m0 0 4-4m-4 4-4-4M5 21h14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <button
            className={`action ${item.allowOnHome ? "on" : ""}`}
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            disabled={busy}
            title={item.allowOnHome ? "Remove from Home" : "Allow on Home"}
          >
            {item.allowOnHome ? "On Home" : "Allow Home"}
          </button>

          <button
            className="action danger icon"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            disabled={deleting}
            aria-label="Delete"
            title="Delete"
          >
            {deleting ? "…" : "🗑"}
          </button>
        </div>
      </div>

      <div className="meta">
        <div className="prompt" title={item.prompt}>
          {item.prompt}
        </div>
        <div className="stamp">{fmt(item.createdAt || Date.now())}</div>
      </div>
    </article>
  );
}

function Lightbox({ item, onClose, onToggle, onDelete, busy, deleting }) {
  const url = getImageUrl(item);
  const hasVideo = !!(item.videoUrl || item.hasVideo);
  const [view, setView] = React.useState(hasVideo ? "video" : "image");

  // If the item changes (different lightbox open), reset view appropriately
  React.useEffect(() => {
    setView(hasVideo ? "video" : "image");
  }, [item.id, hasVideo]);

  const styleId = React.useMemo(() => {
    const s = String(item.style || "").toLowerCase().trim();
    if (!s || s === "default") return "";
    return s;
  }, [item.style]);

  // ✅ Animate-from-gallery (premium feature) — mirrors the Result page flow.
  const [pricing, setPricing] = React.useState(null);
  const [animateState, setAnimateState] = React.useState({ status: "idle" });
  const [motionPrompt, setMotionPrompt] = React.useState("");
  const [credits, setCredits] = React.useState(null);
  const pollRef = React.useRef(null);

  React.useEffect(() => {
    fetch(api("/api/animate/pricing"))
      .then((r) => r.json())
      .then((p) => setPricing(p))
      .catch(() => {});
  }, []);

  // Fetch current credit balance so the user sees affordability before animating
  const refreshCredits = React.useCallback(async () => {
    try {
      await waitForIdentity();
      const res = await fetch(api("/api/credits"), {
        credentials: "include",
        headers: { ...getIdentityHeaders() },
      });
      if (!res.ok) return;
      const j = await res.json();
      if (typeof j.credits === "number") setCredits(j.credits);
    } catch (e) {
      console.warn("Failed to fetch credits:", e);
    }
  }, []);

  React.useEffect(() => { (async () => { await refreshCredits(); })(); }, [refreshCredits]);

  // Cross-page credit sync — mirrors the Create page so balances stay consistent
  React.useEffect(() => {
    const onFocus = () => refreshCredits();
    const onVisibility = () => { if (document.visibilityState === "visible") refreshCredits(); };
    const onStorage = (e) => { if (e.key === "ab:credits:ping") refreshCredits(); };
    const onCustom = () => refreshCredits();

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("storage", onStorage);
    window.addEventListener("ab:credits", onCustom);

    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("ab:credits", onCustom);
    };
  }, [refreshCredits]);

  // Reset animate state when the lightbox opens a different item
  React.useEffect(() => {
    setAnimateState({ status: "idle" });
    setMotionPrompt("");
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [item.id]);

  React.useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  function pollAnimation(jobId) {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await fetch(api(`/api/animate/status/${jobId}`), {
          credentials: "include",
          headers: { ...getIdentityHeaders() },
        });
        const j = await r.json();
        if (j.status === "complete") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setAnimateState({ status: "complete", jobId, videoUrl: j.signedVideoUrl });
          toast("Video ready! 🎬");
        } else if (j.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setAnimateState({ status: "failed", error: j.error });
          toast(`Animation failed: ${j.error}`);
        }
      } catch (e) {
        console.warn("Animate poll error:", e);
      }
    }, 10_000);
    setTimeout(() => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 360_000);
  }

  async function handleAnimate() {
    if (ANIMATION_PAUSED) {
      toast("Animation is paused on testnet.");
      return;
    }
    if (!item?.id || !pricing) return;
    setAnimateState({ status: "processing" });
    try {
      const res = await fetch(api("/api/animate"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getIdentityHeaders() },
        body: JSON.stringify({
          generationId: item.id,
          ...(motionPrompt.trim() ? { prompt: motionPrompt.trim() } : {}),
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.error || `HTTP ${res.status}`);
      toast("Animation started — video in 30–120s");
      setAnimateState({ status: "processing", jobId: j.jobId });
      pollAnimation(j.jobId);
      // Animation deducted credits — refresh locally and notify other pages
      // (Create listens for "ab:credits" / "ab:credits:ping" to stay in sync).
      refreshCredits();
      try {
        localStorage.setItem("ab:credits:ping", String(Date.now()));
        window.dispatchEvent(new Event("ab:credits"));
      } catch {}
    } catch (e) {
      setAnimateState({ status: "failed", error: e.message });
      toast(e.message || "Failed to start animation");
    }
  }

  // ✅ NEW: helper to fetch a fresh signed URL before download (signed URLs expire after 1h)
  async function getFreshVideoUrl() {
    try {
      const r = await fetch(api(`/api/generations/${item.id}`), {
        credentials: "include",
        headers: { ...getIdentityHeaders() },
      });
      const j = await r.json();
      return j?.videoUrl || item.videoUrl;
    } catch {
      return item.videoUrl;
    }
  }

  const onDownload = async () => {
    const downloadUrl = api(`/api/generations/${item.id}/download`);
    
    if (window.Pi && typeof window.Pi.openUrlInSystemBrowser === 'function') {
      try {
        await window.Pi.openUrlInSystemBrowser(downloadUrl);
        toast("Opening in system browser for download");
      } catch (err) {
        console.error("Pi SDK download error:", err);
        if (err.message === "No minimal requirements") {
          toast("Please update Pi Browser to download images");
        } else if (err.message === "Failed to open URL") {
          toast("Invalid image URL");
        } else {
          fallbackDownload();
        }
      }
    } else {
      fallbackDownload();
    }
    
    async function fallbackDownload() {
      try {
        const res = await fetch(downloadUrl, {
          method: "GET",
          headers: { ...getIdentityHeaders() },
          credentials: "include",
        });
        if (!res.ok) throw new Error(`Download failed (${res.status})`);
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `artibytes-${item.id}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(blobUrl);
      } catch (err) {
        toast(err.message || "Download failed");
      }
    }
  };

  // ✅ NEW: download the animation video
  const onDownloadVideo = async () => {
    try {
      const freshUrl = await getFreshVideoUrl();
      if (!freshUrl) throw new Error("Video URL unavailable");

      if (window.Pi && typeof window.Pi.openUrlInSystemBrowser === 'function') {
        try {
          await window.Pi.openUrlInSystemBrowser(freshUrl);
          toast("Opening in system browser for download");
          return;
        } catch (err) {
          console.error("Pi SDK video download error:", err);
          if (err.message === "No minimal requirements") {
            toast("Please update Pi Browser to download videos");
            return;
          }
          // fall through to blob
        }
      }

      const res = await fetch(freshUrl);
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `artibytes-${item.id}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast("Downloaded!");
    } catch (err) {
      toast(err.message || "Video download failed");
    }
  };

  return (
    <div className="lb-backdrop" onClick={onClose}>
      <div className="lb-dialog" onClick={(e) => e.stopPropagation()}>
        {/* Media area — image OR video, switchable when both exist */}
        <div className="lb-media">
          {view === "video" && hasVideo && item.videoUrl ? (
            <video
              src={item.videoUrl}
              poster={url}
              controls
              autoPlay
              loop
              playsInline
              style={{ width: "100%", height: "100%", objectFit: "contain", background: "#000" }}
            />
          ) : (
            <img src={url} alt={item.prompt} />
          )}

          {/* ✅ NEW: image/video toggle (only shown when both exist) */}
          {hasVideo && (
            <div className="lb-media-toggle">
              <button
                className={`lb-toggle-btn ${view === "image" ? "active" : ""}`}
                onClick={() => setView("image")}
              >
                Image
              </button>
              <button
                className={`lb-toggle-btn ${view === "video" ? "active" : ""}`}
                onClick={() => setView("video")}
              >
                🎬 Video
              </button>
            </div>
          )}
        </div>

        <div className="lb-info">
          <div className="lb-row">
            {styleId ? (
              <Link
                to={`/creator/styles/${encodeURIComponent(styleId)}`}
                className="lb-style lb-style-link"
                title={`View generations using "${styleId}"`}
              >
                {styleId} <span aria-hidden="true">↗</span>
              </Link>
            ) : (
              <span className="lb-style">default</span>
            )}
            <span className="lb-date">{fmt(item.createdAt || Date.now())}</span>
          </div>

          <div className="lb-prompt">{item.prompt}</div>

          <div className="lb-actions">
            <button className="btn btn-primary" onClick={onDownload}>
              Download
            </button>
            {/* ✅ NEW: Download video — primary styling, only when video exists */}
            {hasVideo && (
              <button className="btn btn-primary" onClick={onDownloadVideo}>
                Download video
              </button>
            )}
            <button
              className="btn"
              onClick={onToggle}
              disabled={busy}
              style={item.allowOnHome ? { borderColor: brand.purple, color: brand.purple } : undefined}
            >
              {item.allowOnHome ? "Remove from Home" : "Allow on Home"}
            </button>
            <button className="btn danger" onClick={onDelete} disabled={deleting} title="Delete permanently">
              {deleting ? "Deleting…" : "Delete"}
            </button>
            <button className="btn" onClick={onClose}>
              Close
            </button>
          </div>

          {/* ✅ NEW: Animate this image (premium) — only when it has no video yet */}
          {!hasVideo && animateState.status !== "complete" && (
            <div className="lb-animate">
              {animateState.status === "idle" && (
                <>
                  <input
                    type="text"
                    className="lb-animate-input"
                    value={motionPrompt}
                    onChange={(e) => setMotionPrompt(e.target.value)}
                    placeholder="Describe the motion (optional) — e.g. slow zoom in, hair blowing"
                    maxLength={300}
                  />
                  <button
                    className="btn btn-primary lb-animate-btn"
                    onClick={handleAnimate}
                    disabled={ANIMATION_PAUSED || !pricing || (typeof credits === "number" && pricing && credits < pricing.credits)}
                  >
                    {pricing
                      ? `🎬 Animate (${pricing.durationSeconds}s — ${pricing.credits} credits)`
                      : "🎬 Animate"}
                  </button>
                  {ANIMATION_PAUSED ? (
                    <div className="lb-animate-hint">
                      <span className="lb-animate-hint-paused">Animation paused on testnet</span>
                    </div>
                  ) : (
                    pricing && (
                      <div className="lb-animate-hint">
                        {pricing.credits} credits per animation
                        {typeof credits === "number" && (
                          <span
                            className={credits < pricing.credits ? "lb-animate-hint-low" : "lb-animate-hint-avail"}
                          >
                            {" • "}Available: {credits}
                          </span>
                        )}
                      </div>
                    )
                  )}
                </>
              )}
              {animateState.status === "processing" && (
                <div className="lb-animate-status">
                  <div className="lb-animate-spinner" />
                  <span>Animating… video ready in 30–120s. You can keep browsing.</span>
                </div>
              )}
              {animateState.status === "failed" && (
                <div className="lb-animate-failed">
                  Animation failed: {animateState.error}. Credits were refunded.
                  <button className="btn lb-animate-retry" onClick={() => setAnimateState({ status: "idle" })}>
                    Try again
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ✅ NEW: freshly-created video plays inline on completion */}
          {animateState.status === "complete" && animateState.videoUrl && (
            <div className="lb-animate-result">
              <video
                src={animateState.videoUrl}
                poster={url}
                controls
                autoPlay
                loop
                playsInline
                style={{ width: "100%", borderRadius: 12, background: "#000" }}
              />
              <div className="lb-animate-done">🎬 Animation complete — find it in your gallery anytime.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ onClear, hasFilters }) {
  return (
    <div className="empty">
      <h3>{hasFilters ? "No results" : "No images yet"}</h3>
      <p>
        {hasFilters
          ? "Try clearing search/filters or generate something new."
          : "Head to Create and generate your first image!"}
      </p>
      <div className="empty-actions">
        {hasFilters && (
          <button className="btn" onClick={onClear}>
            Reset filters
          </button>
        )}
        <a className="btn btn-primary" href="/create">
          Create image
        </a>
      </div>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="masonry">
      {Array.from({ length: 10 }).map((_, i) => (
        <div key={i} className="masonry-card">
          <div className="sk-img" />
          <div className="meta">
            <div className="sk-line" />
            <div className="sk-line short" />
          </div>
        </div>
      ))}
    </div>
  );
}

function StyleTag() {
  return (
    <style>{`
.gal-header {
  display:flex;
  align-items:flex-end;
  justify-content:space-between;
  gap:24px;
  flex-wrap:wrap;
  margin-bottom: 16px;
}
.gal-controls {
  display:flex;
  align-items:center;
  gap:12px;
  flex-wrap:wrap;
}
.err-note{
  margin-top:8px;
  color:#B91C1C;
  background:#FEF2F2;
  border:1px solid #FECACA;
  padding:8px 10px;
  border-radius:10px;
  font-size:13px;
}

.filter-info{
  display:flex;
  align-items:center;
  gap:12px;
  flex-wrap:wrap;
  padding:12px 16px;
  background:linear-gradient(135deg, #F3F0FF 0%, #E7E2FF 100%);
  border:1px solid #D4C5F9;
  border-radius:12px;
  margin-bottom:16px;
  font-size:14px;
  font-weight:600;
  color:#5B4EE8;
}
.filter-badge{
  display:inline-flex;
  align-items:center;
  padding:4px 10px;
  background:#fff;
  border:1px solid #D4C5F9;
  border-radius:999px;
  font-size:12px;
  font-weight:700;
  color:#6D5EF9;
}
.clear-filters{
  margin-left:auto;
  padding:4px 12px;
  background:#fff;
  border:1px solid #D4C5F9;
  border-radius:999px;
  font-size:12px;
  font-weight:700;
  color:#6D5EF9;
  cursor:pointer;
  transition: all 0.2s;
}
.clear-filters:hover{
  background:#6D5EF9;
  color:#fff;
  border-color:#6D5EF9;
}

.info-banner{
  display:flex;
  gap:14px;
  align-items:flex-start;
  background: linear-gradient(135deg, #F3F0FF 0%, #E7E2FF 100%);
  border:1px solid #D4C5F9;
  border-radius:14px;
  padding:16px;
  margin-bottom:20px;
  position:relative;
}
.info-banner-icon{ font-size:28px; flex-shrink:0; }
.info-banner-content{ flex:1; }
.info-banner-content h3{
  margin:0 0 8px 0;
  font-size:16px;
  font-weight:800;
  color:#5B4EE8;
}
.info-banner-content p{
  margin:0 0 8px 0;
  font-size:14px;
  line-height:1.5;
  color:#6D5EF9;
}
.info-banner-content p:last-of-type{ margin-bottom:12px; }
.info-banner-actions{ display:flex; gap:8px; flex-wrap:wrap; }
.info-banner-close{
  position:absolute; top:12px; right:12px;
  width:28px; height:28px; border-radius:50%;
  border:none; background:rgba(109,94,249,0.1);
  color:#5B4EE8; font-size:20px; font-weight:700;
  cursor:pointer; display:grid; place-items:center;
  transition: all 0.2s;
}
.info-banner-close:hover{ background:rgba(109,94,249,0.2); }

.btn-sm{
  height:34px; padding:0 12px; font-size:13px;
  border-radius:10px; border:1px solid #6D5EF9;
  background:#fff; color:#5B4EE8; font-weight:700;
  text-decoration:none; display:inline-flex;
  align-items:center; transition: all 0.2s;
}
.btn-sm:hover{ background:#F3F0FF; }

.gal-search input{
  height:42px; padding:10px 14px; border-radius:10px;
  border:1px solid ${brand.neutralBorder};
  outline:none; min-width: 260px;
}
.gal-search input:focus{ border-color:${brand.purple}; box-shadow:0 0 0 3px ${brand.purpleTint}; }

.gal-sort label{ display:flex; gap:8px; align-items:center; font-size:14px; font-weight:700; }
.gal-sort select{
  height:42px; border-radius:10px; border:1px solid ${brand.neutralBorder};
  padding:8px 12px; font-weight:600; min-width: 200px;
}
.gal-sort select:focus{ border-color:${brand.purple}; box-shadow:0 0 0 3px ${brand.purpleTint}; outline:none; }

.masonry{ column-count: 1; column-gap: 16px; }
@media (min-width: 640px){ .masonry{ column-count: 2; } }
@media (min-width: 980px){ .masonry{ column-count: 3; } }
@media (min-width: 1280px){ .masonry{ column-count: 4; } }

.masonry-card{
  break-inside: avoid;
  background: #fff;
  border-radius:14px;
  border:1px solid ${brand.neutralBorder};
  margin: 0 0 16px;
  overflow:hidden;
  box-shadow:${brand.shadow};
  cursor:pointer;
  transition: transform .15s ease;
}
.masonry-card:hover{ transform: translateY(-2px); }

.img-wrap{ position:relative; overflow:hidden; }
.img-wrap img{
  position:relative; z-index:1;
  width:100%; height:auto; display:block;
  transform: translateZ(0);
  filter: blur(16px); opacity: .6;
  transition: filter .35s ease, opacity .35s ease;
}
.img-wrap img.ready{ filter: blur(0); opacity: 1; }

.style-link{
  text-decoration:none;
  transition: transform .15s ease, box-shadow .15s ease;
  cursor:pointer;
}
.style-link:hover{
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(0,0,0,.15);
}
.style-chip{
  position:absolute; top:10px; left:10px;
  z-index:5; pointer-events:auto;
  background:${brand.purple};
  color:#fff; font-weight:800; font-size:12px;
  padding:6px 10px; border-radius:999px;
  box-shadow:${brand.shadow};
  display:inline-flex; align-items:center; gap:6px;
}
.style-ext{
  font-size:12px; opacity:0.9;
  transform: translateY(-0.5px);
}

.home-chip{
  position:absolute; top:10px; right:10px; z-index:5;
  background:#fff; color:${brand.purple}; font-weight:800; font-size:11px;
  padding:6px 10px; border-radius:999px;
  border:1px solid ${brand.purpleBorder};
  box-shadow:${brand.shadow};
}

/* ✅ NEW: video badge */
.video-chip{
  position:absolute; bottom:10px; left:10px; z-index:5;
  background:rgba(0,0,0,0.78);
  color:#fff; font-weight:800; font-size:11px;
  padding:6px 10px; border-radius:999px;
  box-shadow:${brand.shadow};
  display:inline-flex; align-items:center; gap:6px;
  pointer-events:none;
  transition: opacity .18s ease;
}
.img-wrap:hover .video-chip{ opacity: 0; }

.hover-bar{
  position:absolute; inset:auto 10px 10px 10px; z-index:6;
  display:flex; gap:8px; justify-content:flex-end;
  opacity:0; transform: translateY(6px);
  transition: all .18s ease;
}
.img-wrap:hover .hover-bar{ opacity:1; transform: translateY(0); }

.action{
  background:#fff; padding:6px 10px; border-radius:10px;
  border:1px solid ${brand.neutralBorder}; font-weight:600; font-size:13px;
  display:inline-flex; align-items:center; gap:6px; height:32px;
}
.action.icon{ padding:6px; width:32px; justify-content:center; }
.action.on{ border-color:${brand.purple}; color:${brand.purple}; background:${brand.purpleTint}; }
.action.danger{ border-color:#FECACA; }

.meta{ padding:10px 12px 12px; display:grid; gap:6px; }
.prompt{ font-weight:600; font-size:14px; color:#111827; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.stamp{ font-size:12px; color:#6B7280; }

.load-more{ display:flex; justify-content:center; margin: 8px 0 24px; }

.lb-backdrop{
  position:fixed; inset:0; background:rgba(17,24,39,.6);
  display:grid; place-items:center; z-index:60;
}
.lb-dialog{
  background:#fff; border-radius:16px; overflow:hidden;
  max-width: min(1080px, 92vw);
  box-shadow: 0 20px 50px rgba(0,0,0,.25);
  display:grid; grid-template-columns: 1fr 360px; gap:0;
}
@media (max-width: 900px){
  .lb-dialog{ grid-template-columns: 1fr; max-height: 90vh; overflow:auto; }
}

/* ✅ NEW: media container holds either image or video */
.lb-media{
  position: relative;
  background:#000;
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.lb-media img{
  width:100%; height:100%; object-fit:contain; background:#000;
}

/* ✅ NEW: image/video switcher */
.lb-media-toggle{
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 5;
  display: flex;
  gap: 4px;
  background: rgba(0,0,0,0.5);
  border-radius: 999px;
  padding: 3px;
  backdrop-filter: blur(8px);
}
.lb-toggle-btn{
  border: none;
  background: transparent;
  color: rgba(255,255,255,0.7);
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
}
.lb-toggle-btn:hover{ color: #fff; }
.lb-toggle-btn.active{
  background: #fff;
  color: ${brand.purple};
}

.lb-info{ padding:16px; display:grid; gap:12px; }
.lb-row{ display:flex; justify-content:space-between; align-items:center; }

.lb-style{
  background:${brand.purpleTint};
  color:${brand.purple};
  border:1px solid ${brand.purpleBorder};
  padding:6px 10px; border-radius:999px;
  font-weight:800; font-size:12px;
  display:inline-flex; gap:6px; align-items:center;
}
.lb-style-link{ text-decoration:none; }
.lb-style-link:hover{
  background:${brand.purple};
  color:#fff;
  border-color:${brand.purple};
}

.lb-date{ color:#6B7280; font-size:12px; }
.lb-prompt{ font-weight:600; }
.lb-actions{ display:flex; gap:10px; margin-top:8px; flex-wrap:wrap; }
.btn{
  height:40px; padding:0 14px; border-radius:12px;
  border:1px solid ${brand.neutralBorder}; background:#fff; font-weight:700;
}
.btn-primary{ background:${brand.purple}; color:#fff; border-color:${brand.purple}; }
.btn.danger{ border-color:#FECACA; background:#FEF2F2; color:#B91C1C; }

/* ✅ Animate-from-gallery */
.lb-animate{ margin-top:12px; display:flex; gap:10px; flex-wrap:wrap; align-items:center;
  padding-top:12px; border-top:1px solid ${brand.neutralBorder}; }
.lb-animate-input{ flex:1 1 240px; min-width:200px; height:40px; padding:0 12px;
  border:1px solid ${brand.neutralBorder}; border-radius:12px; font-size:14px; }
.lb-animate-btn{ white-space:nowrap; }
.lb-animate-hint{ flex-basis:100%; font-size:13px; color:#6B7280; margin-top:4px; }
.lb-animate-hint-avail{ color:#6B7280; }
.lb-animate-hint-low{ color:#B91C1C; font-weight:600; }
.lb-animate-hint-paused{ color:#6D5EF9; font-weight:600; }
.lb-animate-status{ display:flex; align-items:center; gap:10px; color:${brand.purple};
  font-weight:600; font-size:14px; }
.lb-animate-spinner{ width:18px; height:18px; border:3px solid #EDEAFF;
  border-top-color:${brand.purple}; border-radius:50%; animation:spin 0.9s linear infinite; }
@keyframes spin{ to{ transform:rotate(360deg); } }
.lb-animate-failed{ color:#B91C1C; font-weight:600; font-size:14px; display:flex;
  align-items:center; gap:10px; flex-wrap:wrap; }
.lb-animate-retry{ height:32px; padding:0 12px; }
.lb-animate-result{ margin-top:12px; padding-top:12px; border-top:1px solid ${brand.neutralBorder}; }
.lb-animate-done{ margin-top:8px; font-size:13px; color:${brand.purple}; font-weight:600; text-align:center; }

.sk-img{ height: 220px; background: linear-gradient(90deg,#f5f5f7, #ececf2, #f5f5f7); animation: sk 1.2s infinite; }
.sk-line{ height:12px; margin-top:8px; background:#EEE; border-radius:8px; }
.sk-line.short{ width: 40%; }
@keyframes sk { 0%{background-position:-200px 0} 100%{background-position:calc(200px + 100%) 0} }

.empty{ text-align:center; padding: 64px 16px; border:1px dashed ${brand.neutralBorder}; border-radius:14px; }
.empty-actions{ display:flex; justify-content:center; gap:12px; margin-top:10px; flex-wrap:wrap; }
    `}</style>
  );
}